import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from 'lucide-react';
import { searchCustomerUsers } from '../../../services/apiClient';
import { Input } from '../../../components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '../../../components/ui/popover';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * Email combobox for the "add user to tenant" flow: debounces keystrokes
 * into a server-side search (GET .../users/search, which already excludes
 * users already in this tenant) and renders matches in a dropdown. A pick
 * is required -- `onSelectionChange` only fires with a real
 * {user_id, email} on click, and with null the moment the admin edits the
 * text afterward, so the parent's "Add" button can never submit a
 * free-typed, unverified email.
 *
 * Remount via a changing `key` prop (see UsersTab) to reset after a
 * successful add.
 */
const UserAutocomplete = ({ customerId, onSelectionChange, disabled }) => {
  const { t } = useTranslation('admin');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const requestIdRef = useRef(0);
  const skipNextSearchRef = useRef(false);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return undefined;
    }
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return undefined;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    const timer = setTimeout(() => {
      searchCustomerUsers(customerId, trimmed)
        .then((users) => {
          if (requestIdRef.current !== requestId) return; // superseded by a later keystroke
          setResults(users);
          setOpen(true);
          setErr(null);
        })
        .catch((e) => {
          if (requestIdRef.current !== requestId) return;
          setErr(t('errors.searchUsers', { detail: e.message }));
        })
        .finally(() => {
          if (requestIdRef.current === requestId) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, customerId]);

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
    onSelectionChange(null);
  };

  const handleSelect = (user) => {
    skipNextSearchRef.current = true;
    setQuery(user.email);
    setResults([]);
    setOpen(false);
    onSelectionChange(user);
  };

  const showNoResults = !loading && !err && query.trim().length >= MIN_QUERY_LENGTH && results.length === 0;

  return (
    <div className="flex-1">
      <Popover open={open && results.length > 0} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <Input
            type="text"
            value={query}
            onChange={handleQueryChange}
            placeholder={t('tenants.searchPlaceholder')}
            disabled={disabled}
            className="w-full"
          />
        </PopoverAnchor>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-1"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <ul className="max-h-56 overflow-y-auto">
            {results.map((u) => (
              <li key={u.user_id}>
                <button
                  type="button"
                  onClick={() => handleSelect(u)}
                  className="w-full text-left px-2 py-1.5 text-sm rounded-md hover:bg-gray-100 text-gray-800"
                >
                  {u.email}
                </button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
      {loading && <Loader className="w-3.5 h-3.5 animate-spin text-gray-400 mt-1" />}
      {showNoResults && <div className="text-xs text-gray-400 mt-1">{t('tenants.noSearchResults')}</div>}
      {err && <div className="text-xs text-error-600 mt-1">{err}</div>}
    </div>
  );
};

export default UserAutocomplete;
